import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Prevent the benign "ResizeObserver loop completed with undelivered notifications"
// warning that Radix Select / Popover positioning triggers on open. CRA's dev
// error-overlay (which lives in an iframe wired via webpack-dev-server) treats it
// as an uncaught error and covers the app. Wrapping ResizeObserver so callbacks
// fire in the next animation frame prevents the loop-limit condition entirely
// per the WICG spec — no messages to suppress downstream.
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
  const OrigResizeObserver = window.ResizeObserver;
  class DebouncedResizeObserver extends OrigResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          try { callback(entries, observer); } catch { /* callback errors are the caller's problem */ }
        });
      });
    }
  }
  window.ResizeObserver = DebouncedResizeObserver;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Register the PWA service worker in production only — CRA's dev-server hot-reload
// and the SW's aggressive caching don't mix.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch((err) => console.warn("Service worker registration failed:", err));
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
